import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Send, CheckCircle2, Loader2, Phone, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../../context/LangContext";
import { SERVICES, COMPANY } from "../../lib/data";
import { Reveal, Tag } from "./Reveal";
import { MPattern } from "./MPattern";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", service: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | done

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("done");
      toast.success(t.contact.success);
      setForm({ name: "", email: "", phone: "", company: "", service: "", message: "" });
      setTimeout(() => setStatus("idle"), 4000);
    } catch (err) {
      setStatus("idle");
      toast.error(t.contact.error);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3.5 text-white placeholder-slate-500 focus:border-brand-azure focus:outline-none focus:ring-1 focus:ring-brand-azure/40 transition-colors";

  return (
    <section id="contact" className="relative bg-brand-steel py-24 md:py-32 overflow-hidden" data-testid="contact">
      <MPattern opacity={0.04} />
      <div className="relative max-w-6xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          <Reveal>
            <Tag dark>{t.contact.tag}</Tag>
            <h2 className="mt-5 font-display font-semibold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
              {t.contact.title}
            </h2>
            <p className="mt-6 text-slate-400 text-base md:text-lg leading-relaxed max-w-md">{t.contact.subtitle}</p>

            <div className="mt-8 space-y-3">
              <a href={`tel:${COMPANY.telHref}`} className="flex items-center gap-3 text-slate-200 hover:text-white transition-colors text-sm" data-testid="contact-tel">
                <span className="h-9 w-9 rounded-full bg-white/5 flex items-center justify-center text-brand-azure"><Phone size={16} /></span>
                {COMPANY.tel}
              </a>
              <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-3 text-slate-200 hover:text-white transition-colors text-sm" data-testid="contact-mail">
                <span className="h-9 w-9 rounded-full bg-white/5 flex items-center justify-center text-brand-azure"><Mail size={16} /></span>
                {COMPANY.email}
              </a>
              <div className="flex items-center gap-3 text-slate-300 text-sm">
                <span className="h-9 w-9 rounded-full bg-white/5 flex items-center justify-center text-brand-azure"><MapPin size={16} /></span>
                {COMPANY.registered.lines[0]}, {COMPANY.registered.lines[1]}
              </div>
            </div>

            <div className="mt-8 space-y-3 border-t border-white/10 pt-8">
              {t.certs.items.map((c, i) => (
                <div key={i} className="flex items-center gap-3 text-slate-300 text-sm">
                  <CheckCircle2 size={18} className="text-brand-azure shrink-0" />
                  <span>{c.code} — {c.label}</span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <form onSubmit={submit} className="rounded-3xl border border-white/10 bg-white/[0.02] p-6 md:p-8 space-y-4" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <input className={inputCls} placeholder={t.contact.name + " *"} value={form.name} onChange={update("name")} required data-testid="contact-name" />
                <input className={inputCls} type="email" placeholder={t.contact.email + " *"} value={form.email} onChange={update("email")} required data-testid="contact-email" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input className={inputCls} placeholder={t.contact.phone} value={form.phone} onChange={update("phone")} data-testid="contact-phone" />
                <input className={inputCls} placeholder={t.contact.company} value={form.company} onChange={update("company")} data-testid="contact-company" />
              </div>
              <select className={inputCls + " appearance-none cursor-pointer"} value={form.service} onChange={update("service")} data-testid="contact-service">
                <option value="" className="bg-brand-navy">{t.contact.selectService}</option>
                {SERVICES.map((s) => (
                  <option key={s.id} value={s[lang].name} className="bg-brand-navy">{s[lang].name}</option>
                ))}
              </select>
              <textarea className={inputCls + " resize-none"} rows={4} placeholder={t.contact.message + " *"} value={form.message} onChange={update("message")} required data-testid="contact-message" />

              <motion.button
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={status === "sending"}
                className="group w-full inline-flex items-center justify-center gap-2 font-condensed uppercase tracking-[0.15em] font-semibold text-brand-navy bg-brand-azure hover:bg-white transition-colors rounded-full px-7 py-4 disabled:opacity-60"
                data-testid="contact-submit"
              >
                {status === "sending" ? (
                  <><Loader2 size={18} className="animate-spin" /> {t.contact.sending}</>
                ) : status === "done" ? (
                  <><CheckCircle2 size={18} /> {t.contact.success}</>
                ) : (
                  <>{t.contact.submit} <Send size={16} className="transition-transform group-hover:translate-x-1" /></>
                )}
              </motion.button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
